/**
 * MODULE: _shared/frontend-shell/src/components/assistant/assistantWelcomePrompts.ts
 * PURPOSE: Assistant empty-state subtitle and capability welcome chips (CRM, email, research, templates).
 * RECENT: 2026-06-06 [2.2.1330] — **`assistantWelcomeSubtitle`** + **`ASSISTANT_CAPABILITY_WELCOME_CHIPS`** replace email-only empty state.
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 223 / 250 lines
 */

import type {
  AssistantContextAction,
  AssistantContextResponse,
} from '@shared/lib/frontend/api/assistantContextApi';

/** @deprecated Use ASSISTANT_CAPABILITY_WELCOME_CHIPS — kept for starter prompt fallback strings. */
export const DEFAULT_ASSISTANT_WELCOME_PROMPTS = [
  'Summarize my pipeline and flag deals that need attention',
  'Draft a short follow-up email after a discovery call',
  'Research a company and summarize fit and talk tracks',
  'List my email templates and suggest one for outreach',
] as const;

/** Balanced quick actions across assistant skills (max 4 shown in empty state). */
export const ASSISTANT_CAPABILITY_WELCOME_CHIPS: AssistantWelcomeChip[] = [
  {
    label: 'What should I tackle first?',
    prompt: 'What should I tackle first?',
    kind: 'prompt',
  },
  {
    label: 'Brief me on an account',
    prompt: 'Brief me on Contoso',
    kind: 'prompt',
  },
  {
    label: 'Help with pipe gen',
    prompt: 'Help me with pipe gen',
    kind: 'prompt',
  },
  {
    label: 'Draft a follow-up email',
    prompt: 'Draft a short follow-up email to a prospect after a discovery call',
    kind: 'prompt',
  },
];

/** Empty-state headline describing full assistant scope (not email-only). */
export function assistantWelcomeSubtitle(): string {
  return 'I can brief you, recommend next steps, or take CRM actions — try a chip below or ask anything.';
}

export type AssistantWelcomeChipKind = 'prompt' | 'open_create_modal';

export type AssistantWelcomeChip = {
  /** Short label on the chip button. */
  label: string;
  /** Utterance sent to the assistant (may include recipient email for grounded send). */
  prompt: string;
  /** When open_create_modal, opens EmailTemplateCreateModal instead of sending chat. */
  kind?: AssistantWelcomeChipKind;
  /** Self-learning send count from world-model top_by_uses. */
  uses?: number;
  /** Golden-path template id when chip triggers grounded send. */
  templateId?: string;
  /** Top-ranked template chip (shows Recommended badge). */
  recommended?: boolean;
};

/** Remove inline "(used N×)" suffix — badge renders separately in welcome chip UI. */
export function stripEmailTemplateUsesFromLabel(label: string): string {
  return label.replace(/\s*\(used\s+\d+×\)\s*/i, '').trim();
}

function parseUsesFromLabel(label: string): number | undefined {
  const match = label.match(/\(used\s+(\d+)×\)/i);
  if (!match) return undefined;
  const n = Number(match[1]);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function emailTemplateChipFromAction(
  action: {
    label?: string;
    prompt_text?: string;
    execution_mode?: string;
    action?: string;
    template_id?: string;
    template_uses?: number;
  },
  options?: { recommended?: boolean },
): AssistantWelcomeChip | null {
  const mode = (action.execution_mode || 'prompt').toLowerCase();
  const label = (action.label || '').trim();
  const prompt = actionPromptText(action);
  if (!label) return null;
  if (mode === 'open_email_template_create') {
    return { label, prompt: prompt || label, kind: 'open_create_modal' };
  }
  if (mode !== 'prompt' || !prompt) return null;
  const uses =
    typeof action.template_uses === 'number' && action.template_uses > 0
      ? action.template_uses
      : parseUsesFromLabel(label);
  return {
    label,
    prompt,
    kind: 'prompt',
    uses,
    templateId: action.template_id?.trim() || undefined,
    recommended: options?.recommended,
  };
}

function actionPromptText(action: {
  label?: string;
  prompt_text?: string;
}): string {
  const explicit = (action.prompt_text || '').trim();
  if (explicit) return explicit;
  return (action.label || '').trim();
}

/** Structured actions for proactive advisor / dock chips (includes create-modal). */
export function collectEmailTemplateContextActions(
  context: AssistantContextResponse | null,
): AssistantContextAction[] {
  if (!context) return [];
  const out: AssistantContextAction[] = [];
  const seen = new Set<string>();
  for (const action of context.suggested_next_actions ?? []) {
    if ((action.source || '') !== 'email_template_snapshot') continue;
    const mode = (action.execution_mode || 'prompt').toLowerCase();
    if (mode !== 'prompt' && mode !== 'open_email_template_create') continue;
    const label = (action.label || '').trim();
    if (!label || seen.has(label.toLowerCase())) continue;
    seen.add(label.toLowerCase());
    out.push(action);
    if (out.length >= 3) break;
  }
  return out;
}

/** Email template chips from brain context (source: email_template_snapshot). */
export function collectEmailTemplateWelcomeChips(
  context: AssistantContextResponse | null,
): AssistantWelcomeChip[] {
  if (!context) return [];
  const seen = new Set<string>();
  const out: AssistantWelcomeChip[] = [];
  for (const action of context.suggested_next_actions ?? []) {
    if ((action.source || '') !== 'email_template_snapshot') continue;
    const label = (action.label || '').trim();
    const key = label.toLowerCase();
    if (!label || seen.has(key)) continue;
    const chip = emailTemplateChipFromAction(action, {
      recommended: out.length === 0 && action.action === 'send_email_template',
    });
    if (!chip) continue;
    seen.add(key);
    out.push(chip);
    if (out.length >= 3) break;
  }
  return out;
}

/** @deprecated Use collectEmailTemplateWelcomeChips — labels only. */
export function collectEmailTemplateWelcomePrompts(
  context: AssistantContextResponse | null,
): string[] {
  return collectEmailTemplateWelcomeChips(context).map((c) => c.label);
}

/** Capability-balanced welcome chips (email snapshot actions stay on dock brain rail). */
export function collectAssistantWelcomeChips(
  _context: AssistantContextResponse | null,
): AssistantWelcomeChip[] {
  return ASSISTANT_CAPABILITY_WELCOME_CHIPS.slice(0, 4);
}

/** @deprecated Use collectAssistantWelcomeChips. */
export function collectAssistantWelcomePrompts(
  context: AssistantContextResponse | null,
): string[] {
  return collectAssistantWelcomeChips(context).map((c) => c.label);
}

export function hasEmailTemplateWelcomeHints(context: AssistantContextResponse | null): boolean {
  return collectEmailTemplateWelcomeChips(context).length > 0;
}

export function hasEmailTemplateCreateWelcomeChip(context: AssistantContextResponse | null): boolean {
  return collectEmailTemplateWelcomeChips(context).some((c) => c.kind === 'open_create_modal');
}

/** True when brain surfaced send/list template chips (library is non-empty). */
export function hasExistingEmailTemplateSendChips(
  context: AssistantContextResponse | null,
): boolean {
  return collectEmailTemplateWelcomeChips(context).some((c) => c.kind === 'prompt');
}

export function emailTemplateWelcomeSubtitle(context: AssistantContextResponse | null): string | null {
  if (!hasEmailTemplateWelcomeHints(context)) return null;
  if (hasEmailTemplateCreateWelcomeChip(context)) {
    const hasRankedSend = collectEmailTemplateWelcomeChips(context).some(
      (c) => c.kind === 'prompt' && (c.recommended || (c.uses ?? 0) > 0),
    );
    if (hasRankedSend) {
      return 'Send a proven template, browse your library, or create a new one from the editor.';
    }
    return 'Create your first Sales Hub email template — then send intros from a welcome chip or by asking.';
  }
  return 'Your top Sales Hub email templates are ready — pick a chip to draft an intro or browse templates.';
}

/** Resolve send text for a proactive advisor email-template action chip. */
export function emailTemplateActionSendText(action: {
  label?: string;
  prompt_text?: string;
  source?: string;
}): string {
  if ((action.source || '') !== 'email_template_snapshot') {
    return (action.label || '').trim();
  }
  return actionPromptText(action);
}
